import { store } from '../store/store';
import { 
  newLoadReceived as newUserLoadReceived, 
  updateLoad as updateUserLoad, 
  deleteLoad as deleteUserLoad, 
  setLastSyncTimestamp as setUserLastSyncTimestamp, 
  addBidToLoad as addBidToUserLoad, 
  updateBidStatus as updateUserBidStatus,
  updateLoad  // Add this import
} from '../store/slices/loadSlice';
import { 
  newLoadReceived as newFeedLoadReceived, 
  updateLoad as updateFeedLoad, 
  deleteLoad as deleteFeedLoad, 
  setLastSyncTimestamp as setFeedLastSyncTimestamp, 
  addBidToLoad, 
  updateBidInLoad, 
  updateBidStatus as updateFeedBidStatus
} from '../store/slices/loadFeedSlice';
import { selectUserType, refreshToken, selectUserEntityId, getFirstName } from '../store/slices/authSlice';
import { addNotification, addNotifications, clearProcessedIds } from '../store/slices/notificationSlice';
import { updateCarrierLoad } from '../store/slices/loadCarrierSlice';
import { soundManager } from '../utils/soundManager';
import { updateBillingTransaction } from '../store/slices/billingSlice';
import { updateTelematics } from '../store/slices/telematicsSlice';
import { addTrackingUpdate } from '../store/slices/trackingHistorySlice';
import { addNewMessage, markMessagesAsRead, updateMessagesReadStatus } from '../store/slices/messagingSlice';

// Import other action creators as needed

const eventHandlers = {
  new_load: async (data) => {
    store.dispatch(newUserLoadReceived(data));
    store.dispatch(newFeedLoadReceived(data));
    const userType = selectUserType(store.getState());
    
    if (userType === 'carrier' || userType === 'carrier_admin') {
      if (data.carrier_notification) {
        console.log('dispatching new carrier load notification', data.carrier_notification);
        store.dispatch(addNotification(data.carrier_notification));
        
        try {
          const soundEnabled = localStorage.getItem('soundNotifications') === 'true';
          if (soundEnabled) {
            console.log('Attempting to play notification sound for new load');
            await soundManager.playNotification().catch(() => {
              console.log('Sound notification deferred');
            });
          }
        } catch (error) {
          console.error('Failed to play notification sound:', error);
        }
      }
    } else if (userType === 'customer' || userType === 'customer_admin') {
      if (data.customer_notification) {
        console.log('dispatching new customer load notification', data.customer_notification);
        store.dispatch(addNotification(data.customer_notification));
      }
    }
  },
  update_load: (data) => {
    const state = store.getState();
    const userType = selectUserType(state);
    const userEntityId = selectUserEntityId(state);
    
    // Handle carrier-specific feed updates
    if (userType === 'carrier' || userType === 'carrier_admin') {
      const normalizedStatus = (data.status || '').toLowerCase();
      if (normalizedStatus === 'assigned' || normalizedStatus === 'cancelled') {
        // Remove from both feed and saved loads when load is assigned or cancelled
        store.dispatch(updateFeedLoad({
          ...data,
          status: data.status  // Explicitly set status to ensure removal logic triggers
        }));
        
        // Update in carrier's assigned loads if needed
        store.dispatch(updateCarrierLoad(data));
      } else if (data.action === 'saved') {
        // Update in both feed and saved loads
        store.dispatch(updateFeedLoad({
          ...data.load,
          action: 'saved',
          is_saved: true
        }));
      } else if (data.action === 'unsaved') {
        // Update in both feed and saved loads
        store.dispatch(updateFeedLoad({
          ...data.load,
          action: 'unsaved',
          is_saved: false
        }));
      } else {
        // Update in both feed and saved loads
        const updateData = {
          ...data,
          stops: data.stops || [],
          bids: data.bids || []
        };
        
        store.dispatch(updateFeedLoad(updateData));
      }
    }

    // Handle core load updates for customers
    if (userType === 'customer' || userType === 'customer_admin') {
      store.dispatch(updateUserLoad({
        ...data,
        stops: data.stops || [],
        bids: data.bids || []
      }));
    }

    // Handle bid status updates if present
    if (data.bids) {
        const loadId = data.id;  // This is the load ID from our modified backend response
        
        // Update the entire load with new bid statuses
        if (userType === 'customer' || userType === 'customer_admin') {
            store.dispatch(updateUserLoad({
                id: loadId,
                bids: data.bids
            }));
        } else if (userType === 'carrier' || userType === 'carrier_admin') {
            store.dispatch(updateFeedLoad({
                id: loadId,
                bids: data.bids
            }));
        }
    }

    // Handle notifications
    if (data.notifications) {
        const relevantNotifications = data.notifications.filter(
            notification => notification.entity_id === userEntityId
        );
        if (relevantNotifications.length > 0) {
            store.dispatch(addNotifications(relevantNotifications));
        }
    }
  },
  delete_load: (loadId) => {
    store.dispatch(deleteUserLoad(loadId));
    store.dispatch(deleteFeedLoad(loadId));
  },
  sync_timestamp: (timestamp) => {
    store.dispatch(setUserLastSyncTimestamp(timestamp));
    store.dispatch(setFeedLastSyncTimestamp(timestamp));
  },
  new_bid: (data) => {
    const state = store.getState();
    const userType = selectUserType(state);
    const userEntityId = selectUserEntityId(state);

    // Ensure bid data is properly formatted with consistent property names
    const formattedBid = {
      ...data,
      bid_type: data.bid_type || data.bidType || 'yes', // Default to 'yes' if not specified
      // Store both naming conventions to ensure compatibility
      bid_type: data.bid_type || data.bidType || 'yes',
      bidType: data.bid_type || data.bidType || 'yes',
      // Handle both naming conventions for alternative dates
      alt_pickup_date: data.alt_pickup_date || data.altPickupDate,
      altPickupDate: data.alt_pickup_date || data.altPickupDate,
      alt_pickup_time: data.alt_pickup_time || data.altPickupTime,
      altPickupTime: data.alt_pickup_time || data.altPickupTime,
      alt_delivery_date: data.alt_delivery_date || data.altDeliveryDate,
      altDeliveryDate: data.alt_delivery_date || data.altDeliveryDate,
      alt_delivery_time: data.alt_delivery_time || data.altDeliveryTime,
      altDeliveryTime: data.alt_delivery_time || data.altDeliveryTime
    };

    if (userType === 'carrier' || userType === 'carrier_admin') {
      if (data.carrier_id.toString() === userEntityId.substring(1)) {  // Remove the 'R' prefix
        const firstName = getFirstName(state);
        const bidWithBidder = { ...formattedBid, bidder_name: firstName };
        store.dispatch(addBidToLoad({ loadId: data.load_id, bid: bidWithBidder }));
        if (data.carrier_notification) {
          store.dispatch(addNotification(data.carrier_notification));
        }
      }
    } else if (userType === 'customer' || userType === 'customer_admin') {
      store.dispatch(addBidToUserLoad({ loadId: data.load_id, bid: formattedBid }));
      if (data.customer_notification) {
        store.dispatch(addNotification(data.customer_notification));
      }
    }
  },
  new_access_token: (data) => {
    console.log('New access token received via WebSocket');
    
    // Update Redux store (which will automatically persist to localStorage via redux-persist)
    if (data.access_token) {
      store.dispatch(refreshToken.fulfilled(data));
      console.log('Access token updated in Redux store');
    } else {
      console.error('No access token in refresh response');
    }
  },
  load_status_update: (data) => {
    const userType = selectUserType(store.getState());
    const userEntityId = selectUserEntityId(store.getState());

    // Transform the status history to include proper user info
    if (data.load.status_history) {
        data.load.status_history = data.load.status_history.map(history => ({
            ...history,
            user: history.user || {},
            driver: history.driver || {},
            // Format the updatedBy string based on user type and available info
            updatedBy: history.user_type === 'driver'
                ? `Driver ${history.driver?.name || 'Unknown'}`
                : `${history.user_type} ${history.user?.name || 'Unknown'}`
        }));
    }

    // Update the load status in the appropriate store
    if (userType === 'carrier' || userType === 'carrier_admin') {
        store.dispatch(updateCarrierLoad({
            ...data.load,
            status: data.load.status,
            status_history: data.load.status_history
        }));
    } else if (userType === 'customer' || userType === 'customer_admin') {
        store.dispatch(updateLoad({
            ...data.load,
            status: data.load.status,
            status_history: data.load.status_history
        }));
    }

    // Handle notifications
    if (data.notification && data.notification.entity_id === userEntityId) {
        store.dispatch(addNotification(data.notification));
    }
  },
  load_exception_update: (data) => {
    const userType = selectUserType(store.getState());
    const userEntityId = selectUserEntityId(store.getState());

    console.log('Load exception update received:', data);

    // Update the load data in the appropriate store (includes exceptions)
    if (data.load) {
        if (userType === 'carrier' || userType === 'carrier_admin') {
            store.dispatch(updateCarrierLoad({
                ...data.load,
                exceptions: data.load.exceptions,
                has_active_exception: data.load.has_active_exception,
                active_exception_count: data.load.active_exception_count
            }));
        } else if (userType === 'customer' || userType === 'customer_admin') {
            store.dispatch(updateLoad({
                ...data.load,
                exceptions: data.load.exceptions,
                has_active_exception: data.load.has_active_exception,
                active_exception_count: data.load.active_exception_count
            }));
        }
    }

    // Handle notifications
    if (data.notification && data.notification.entity_id === userEntityId) {
        store.dispatch(addNotification(data.notification));
        
        // Play notification sound for exceptions (they're important!)
        try {
            const soundEnabled = localStorage.getItem('soundNotifications') === 'true';
            if (soundEnabled) {
                soundManager.playNotification().catch(() => {
                    console.log('Sound notification deferred');
                });
            }
        } catch (error) {
            console.error('Failed to play notification sound:', error);
        }
    }
  },
  billing_status_update: (data) => {
    const userType = selectUserType(store.getState());
    const userEntityId = selectUserEntityId(store.getState());
    
    // Update billing transaction in store
    store.dispatch(updateBillingTransaction(data.transaction));

    // Handle notification
    if (data.notification && data.notification.entity_id === userEntityId) {
      store.dispatch(addNotification(data.notification));
      
      // Play notification sound if enabled
      try {
        const soundEnabled = localStorage.getItem('soundNotifications') === 'true';
        if (soundEnabled) {
          soundManager.playNotification().catch(() => {
            console.log('Sound notification deferred');
          });
        }
      } catch (error) {
        console.error('Failed to play notification sound:', error);
      }
    }
  },
  load_tracking_update: (data) => {
    const { load_id, location, timestamp, vehicle, speed, fuel_level } = data;
    
    // Update real-time tracking data
    store.dispatch(updateTelematics({
      load_id,
      data: {
        location,
        timestamp,
        vehicle,
        speed,
        fuel_level
      }
    }));

    // Add to tracking history
    store.dispatch(addTrackingUpdate({
      load_id,
      data: {
        latitude: location.latitude,
        longitude: location.longitude,
        created_at: timestamp,
        notes: `Vehicle ${vehicle.id} location update`,
        speed,
        fuel_level,
        engine_status: vehicle.engine_status
      }
    }));
  },
  new_message: (data) => {
    const state = store.getState();
    const userType = selectUserType(state);
    const userEntityId = selectUserEntityId(state);
    
    // Ensure the message has the current user's org ID for proper handling
    const messageWithUserContext = {
      ...data,
      currentUserOrgId: userEntityId,
      // Ensure org_name is always present in both sender and receiver
      sender: {
        ...data.sender,
        org_name: data.sender.org_name
      },
      receiver: {
        ...data.receiver,
        org_name: data.receiver.org_name
      }
    };
    
    // Add message to messaging state
    store.dispatch(addNewMessage(messageWithUserContext));
    
    // Handle notification if present
    if (data.notification && data.notification.entity_id === userEntityId) {
      store.dispatch(addNotification(data.notification));
      
      // Play notification sound if enabled
      try {
        const soundEnabled = localStorage.getItem('soundNotifications') === 'true';
        if (soundEnabled) {
          soundManager.playNotification().catch(() => {
            console.log('Sound notification deferred');
          });
        }
      } catch (error) {
        console.error('Failed to play notification sound:', error);
      }
    }
  },
  message_read: (data) => {
    const state = store.getState();
    const userType = selectUserType(state);
    const userEntityId = selectUserEntityId(state);
    
    // Determine the other org ID based on the message data
    const otherOrgId = data.sender_org_id === userEntityId ? 
      data.receiver_org_id : data.sender_org_id;
    
    // Update message read status in state
    store.dispatch(updateMessagesReadStatus({
      messages: [data],
      orgId: otherOrgId
    }));
  },
  sync_response: (data) => {
    console.log('Sync response received:', data);
    const state = store.getState();
    const userType = selectUserType(state);
    
    // Process synchronized load updates
    if (data.data && data.data.loads) {
      data.data.loads.forEach(loadUpdate => {
        if (userType === 'customer' || userType === 'customer_admin') {
          store.dispatch(updateUserLoad(loadUpdate));
        } else if (userType === 'carrier' || userType === 'carrier_admin') {
          store.dispatch(updateFeedLoad(loadUpdate));
          store.dispatch(updateCarrierLoad(loadUpdate));
        }
      });
    }
    
    // Update last sync timestamp
    if (data.timestamp) {
      localStorage.setItem('lastSyncTimestamp', data.timestamp.toString());
      store.dispatch(setUserLastSyncTimestamp(data.timestamp));
      store.dispatch(setFeedLastSyncTimestamp(data.timestamp));
    }
    
    console.log(`Data synchronization completed: ${data.message}`);
  },
  sync_error: (data) => {
    console.error('Sync error:', data);
    // Could dispatch an error notification here if needed
  }
};

// Add this function to periodically clear processed IDs
const clearProcessedIdsInterval = setInterval(() => {
  store.dispatch(clearProcessedIds());
}, 300000); // Clear every 5 minutes

// Make sure to clear the interval when needed (e.g., on component unmount)
// clearInterval(clearProcessedIdsInterval);

export const handleWebSocketEvent = (event, data) => {
  const handler = eventHandlers[event];
  if (handler) {
    handler(data);
  } else {
    console.warn(`No handler for WebSocket event: ${event}`);
  }
};

export { eventHandlers, clearProcessedIdsInterval };
